import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';

// The Permitting home keeps its search, tab, filters, sort and page in the URL
// (List page template, docs/design-system.md section 7), so a view can be
// shared and survives a reload. List or Board is remembered per person instead.
// A digits-only value in the URL (a Job #, an id) is read back as a number.
const text = z.union([z.string(), z.number()]).transform(String);
const flag = z.union([z.literal('1'), z.literal(1), z.literal(true)]).transform(() => '1' as const);

export const TABS = ['mine', 'residential', 'commercial', 'all'] as const;
export type PermitListTab = (typeof TABS)[number];

export const TAB_LABELS: Record<PermitListTab, string> = {
  mine: 'My work',
  residential: 'Residential',
  commercial: 'Commercial',
  all: 'All',
};

export const SORTS = ['urgency', 'job', 'owner', 'updated'] as const;
export type PermitListSort = (typeof SORTS)[number];

export const permitListSearchSchema = z.object({
  q: text.optional(),
  tab: z.enum(TABS).optional(),
  /** Stage keys, comma-separated (several can be picked), plus notRequired and closed. */
  stage: text.optional(),
  /** Only requests with a blocked permit. */
  blocked: flag.optional(),
  /** Jurisdiction ids, comma-separated. */
  jurisdiction: text.optional(),
  /** Owner user ids, comma-separated. */
  owner: text.optional(),
  /** Only requests whose next action is overdue. */
  overdue: flag.optional(),
  /** Requester user ids, comma-separated. */
  requestedBy: text.optional(),
  /** Only requests with plans in PlanGrid. */
  planGrid: flag.optional(),
  /** Only requests where the engineer of record submits the plans. */
  engineer: flag.optional(),
  /** Customer names, separated by "|" (a name can have a comma). */
  customer: text.optional(),
  /** Permit request ids, comma-separated (the board's cards are permits). */
  request: text.optional(),
  sort: z.enum(SORTS).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
  page: z.coerce.number().int().min(0).optional(),
});

export type PermitListSearch = z.infer<typeof permitListSearchSchema>;
export type PermitListFilterKey =
  | 'stage'
  | 'blocked'
  | 'jurisdiction'
  | 'owner'
  | 'overdue'
  | 'requestedBy'
  | 'planGrid'
  | 'engineer'
  | 'customer'
  | 'request';

export const DEFAULT_TAB: PermitListTab = 'mine';
/** Overdue next actions first, then the soonest due. */
export const DEFAULT_SORT: PermitListSort = 'urgency';

export const FILTER_KEYS: PermitListFilterKey[] = [
  'stage',
  'blocked',
  'jurisdiction',
  'owner',
  'overdue',
  'requestedBy',
  'planGrid',
  'engineer',
  'customer',
  'request',
];

export const splitList = (value?: string) => (value ? value.split(',').filter(Boolean) : []);
export const joinList = (values: string[]) => (values.length ? values.join(',') : undefined);
export const splitNames = (value?: string) => (value ? value.split('|').filter(Boolean) : []);
export const joinNames = (values: string[]) => (values.length ? values.join('|') : undefined);

export const activeFilterKeys = (search: PermitListSearch) =>
  FILTER_KEYS.filter((key) => search[key] !== undefined);

/** Filters shown under "More filters" on desktop; the rest have their own toolbar button. */
export const MORE_FILTER_KEYS: PermitListFilterKey[] = [
  'overdue',
  'requestedBy',
  'customer',
  'request',
  'planGrid',
  'engineer',
];

/** How many of the "More filters" are applied, for "More filters (2)". */
export const moreFilterCount = (search: PermitListSearch) =>
  activeFilterKeys(search).filter((key) => MORE_FILTER_KEYS.includes(key)).length;

/** "More filters", or "More filters (2)" once any are applied. */
export const moreFiltersLabel = (count: number) =>
  count > 0 ? `More filters (${count})` : 'More filters';

export const usePermitListSearch = () => {
  const search = useSearch({ from: '/permitting' });
  const navigate = useNavigate({ from: '/permitting' });

  // Any change other than paging goes back to the first page.
  const setSearch = (patch: Partial<PermitListSearch>) =>
    navigate({
      search: (prev) => {
        const next: PermitListSearch = { ...prev, page: undefined, ...patch };
        for (const key of Object.keys(next) as (keyof PermitListSearch)[]) {
          if (next[key] === undefined || next[key] === '') delete next[key];
        }
        return next;
      },
      replace: true,
    });

  return { search, setSearch };
};
